import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	Ref,
	useCallback,
	useRef,
	useState,
	RefObject,
	useLayoutEffect
 } from "react";
import {
	Anchor,
	DateString,
	Icon,
	IconValue,
	Space
} from "client/Components";
import styles from "./EventCard.module.less";
import { MeetingProductKind } from "client/Api/Notifications/notifications";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import classNames from "classnames";
import { localize } from "client_legacy/ndaw/culture";
import { ViewAppointmentBox } from "../ViewAppointmentBox/ViewAppointmentBox";
import { productToIconMap } from "../../OnlineMeetingButton";
import { CalendarStylesColor, Coordinate } from "client/Misc/commonTypes";
import { TimeZone } from "client/DateAndTime/timezones";
import { useDoubleClick } from "client/Hooks";
import { EditAppointmentInfo } from "../../CalendarPage";
import { AppointmentOrIdentifier, isAppointmentInformation } from "../../common";

export type EventCardColor =
	| "info"
	| "success"
	| "warning"
	| "danger"
	| CalendarStylesColor;

export type EventCardVariant =
	| "extra-small"
	| "small"
	| "normal";

type EventCardProps = {
	color?: EventCardColor;
	appointmentOrIdentifier: AppointmentOrIdentifier;
	trackingId: string;
	tooltip: string;
	timezone: TimeZone;
	variant: EventCardVariant;
	onOpenDetailsDialog: () => void;
	hasDashedBorder: boolean;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
	hasColoredIndicator?: boolean;
};

export const EventCard = (props: PropsWithChildren<EventCardProps>): ReactElement => {
	const {
		color,
		appointmentOrIdentifier,
		trackingId,
		tooltip,
		timezone,
		children,
		variant,
		onOpenDetailsDialog,
		hasDashedBorder,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId,
		hasColoredIndicator = false
	} = props;

	const [viewAppointmentBoxLocation, setViewAppointmentBoxLocation] = useState<Coordinate | null>(null);

	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const openDetailsDialogHandler = useCallback(
		(): void => {
			setViewAppointmentBoxLocation(null);
			onOpenDetailsDialog();
		},
		[onOpenDetailsDialog]
	);

	const openAppointmentBoxHandler = useCallback(
		(args: MouseEvent): void => {
			setViewAppointmentBoxLocation({
				x: args.clientX,
				y: args.clientY
			});
		},
		[]
	);

	const [clickHandler, isDoubleClickPending] = useDoubleClick({
		onDoubleClick: (): void => openDetailsDialogHandler(),
		onSingleClick: openAppointmentBoxHandler
	});

	const colorStyle: string | undefined = isNotNil(color)
		? eventCardColorToStylesMap[color]
		: undefined;

	const isCurrentEditingTarget: boolean = !isAppointmentInformation(appointmentOrIdentifier)
		&& appointmentOrIdentifier.id === editingAppointmentId;

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					colorStyle,
					eventCardVariantToStyle[variant],
					{
						[styles.root_active]: viewAppointmentBoxLocation
							|| isCurrentEditingTarget
							|| isDoubleClickPending,
						[styles.root_dashed]: hasDashedBorder,
						[styles.root_hasColoredIndicator]: hasColoredIndicator
					}
				)}
				ref={ref}
				onClick={clickHandler}
				title={tooltip}
			>
				{children}
			</div>
			{viewAppointmentBoxLocation && ref.current && (
				<ViewAppointmentBox
					trackingId={trackingId}
					color={color ?? "no-color"}
					appointmentOrIdentifier={appointmentOrIdentifier}
					onClose={() => setViewAppointmentBoxLocation(null)}
					onEnlarge={openDetailsDialogHandler}
					target={ref.current}
					coordinate={viewAppointmentBoxLocation}
					timezone={timezone}
					onEdit={onEdit}
					onAfterCancelOrDelete={onAfterMutatingAppointment}
				/>
			)}
		</>
	);
};
EventCard.displayName = "EventCard";

export type EventCardPreSuffixProps = {
	kind: "prefix" | "suffix";
	date: string | Date;
	timezone: TimeZone;
};

const EventCardPreSuffix = (props: EventCardPreSuffixProps): ReactElement => {
	const {
		kind,
		date,
		timezone
	} = props;

	const isPrefix: boolean = kind === "prefix";

	return (
		<span className={styles.preSuffix}>
			{isPrefix && "<"}
			<span className={styles.preSuffixDate}>
				{isPrefix && <Space />}
				<DateString
					value={date}
					timezone={timezone}
					format="DM"
				/>
				{!isPrefix && <Space />}
			</span>
			{!isPrefix && ">" }
		</span>
	);
};
EventCardPreSuffix.displayName = "EventCardPreSuffix";

type EventCardIconsProps = {
	icons: readonly IconValue[];
};

const EventCardIcons = (props: EventCardIconsProps): ReactElement | null => {
	const {
		icons
	} = props;

	if (icons.length === 0) {
		return null;
	}

	return (
		<div className={styles.icons}>
			{icons.map((x, index) => (
				<Icon
					key={index}
					value={x}
				/>
			))}
		</div>
	);
};
EventCardIcons.displayName = "EventCardIcons";

const eventCardVariantToStyle: { [key in EventCardVariant]: string | undefined } = {
	"extra-small": styles.root_xSmall,
	"small": styles.root_small,
	"normal": styles.root_normal
};

type EventCardInfoProps = {
	subject: string;
	subtext?: string;
	isCanceled: boolean;
	cardVisualHeightInMinutes: number;
};

const EventCardInfo = (props: EventCardInfoProps): ReactElement => {
	const {
		subject,
		subtext,
		isCanceled,
		cardVisualHeightInMinutes
	} = props;

	return (
		<div
			className={styles.info}
			style={{ ["--_card-visual-height-minutes"]: cardVisualHeightInMinutes }}
		>
			<p
				{...setTrackingId("title")}
				className={classNames(
					styles.title,
					{
						[styles.title_cancelled]: isCanceled
					}
				)}
			>
				{subject}
			</p>

			{subtext && (
				<p
					{...setTrackingId("subtext")}
					className={styles.subtext}>
					{subtext}
				</p>
			)}
		</div>
	);
};
EventCardInfo.displayName = "EventCardInfo";

type EventCardJoinButtonProps = {
	product: MeetingProductKind;
	url: string;
	trackingId?: string;
};

const EventCardJoinButton = (props: EventCardJoinButtonProps): ReactElement | null => {
	const {
		product,
		url,
		trackingId
	} = props;

	const [isButtonVisible, setIsButtonVisible] = useState<boolean>(true);
	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const buttonRef: RefObject<HTMLAnchorElement | null> = useRef<HTMLAnchorElement>(null);

	useLayoutEffect(
		(): void => {
			if (!containerRef.current || !buttonRef.current) {
				return;
			}

			const {
				offsetHeight: height
			} = containerRef.current;

			const {
				offsetHeight: buttonHeight
			} = buttonRef.current;

			const result: boolean = height >= buttonHeight;

			setIsButtonVisible(result);
		},
		[]
	);

	const clickHandler = useCallback(
		(args: MouseEvent) => {
			args.stopPropagation();
		},
		[]
	);

	if (!isButtonVisible) {
		return null;
	}

	return (
		<div
			ref={containerRef}
			className={styles.joinButtonContainer}
		>
			<Anchor
				ref={buttonRef}
				className={styles.joinButton}
				onClick={clickHandler}
				href={url}
				trackingId={trackingId}
				target="_blank"
			>
				<Icon value={productToIconMap[product]} />
				<span className={styles.joinButtonText}>
					{localize("_Join")}
				</span>
			</Anchor>
		</div>
	);
};
EventCardJoinButton.displayName = "EventCardJoinButton";

EventCard.PreSuffix = EventCardPreSuffix;
EventCard.JoinButton = EventCardJoinButton;
EventCard.Icons = EventCardIcons;
EventCard.Info = EventCardInfo;

export const eventCardColorToStylesMap: { [key in EventCardColor]: string } = {
	success: styles.root_success,
	info: styles.root_info,
	danger: styles.root_error,
	warning: styles.root_warning,
	"calendar-1": styles.root_calendar1,
	"calendar-2": styles.root_calendar2,
	"calendar-3": styles.root_calendar3,
	"calendar-4": styles.root_calendar4,
	"calendar-5": styles.root_calendar5,
	"calendar-6": styles.root_calendar6,
	"calendar-7": styles.root_calendar7,
	"calendar-8": styles.root_calendar8,
	"calendar-9": styles.root_calendar9,
	"calendar-10": styles.root_calendar10,
	"calendar-11": styles.root_calendar11,
	"calendar-12": styles.root_calendar12,
	"calendar-13": styles.root_calendar13,
	"calendar-14": styles.root_calendar14,
	"calendar-15": styles.root_calendar15,
	"calendar-16": styles.root_calendar16
};
